import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../ui/tabs";
import { getProficiencyForStudent, upsertProficiencyRecord, overrideAchieved, setStudentThresholdOverride } from "../../lib/api";

const ACADEMIC_SCORES = [
  { value: "N/A", label: "N/A — Not addressed this period" },
  { value: "1", label: "1 — Taught & assessed, below proficiency" },
  { value: "2", label: "2 — Approaching proficiency" },
  { value: "3", label: "3 — Demonstrated proficiency (3.X)" },
];

const TRANSFERABLE_SCORES = [
  { value: "N/A", label: "N/A — Not addressed" },
  { value: "Infrequently", label: "Infrequently" },
  { value: "Sometimes", label: "Sometimes" },
  { value: "Regularly", label: "Regularly" },
  { value: "Consistently", label: "Consistently" },
];

function currentSemester() {
  // Default: derive from today's month — Aug-Dec = Fall, Jan-Jul = Spring
  const d = new Date();
  const m = d.getMonth();
  const y = d.getFullYear();
  if (m >= 7) return { semester: `Fall-${y}`, school_year: `${y}-${y + 1}` };
  return { semester: `Spring-${y}`, school_year: `${y - 1}-${y}` };
}

function StatusBadge({ achieved, override }) {
  if (achieved && override === true) {
    return <Badge className="bg-green-600" data-testid="status-achieved-manual">Achieved (manual)</Badge>;
  }
  if (achieved) {
    return <Badge className="bg-green-600" data-testid="status-achieved">Achieved</Badge>;
  }
  if (override === false) {
    return <Badge variant="outline" className="border-amber-500 text-amber-700">Not yet (override)</Badge>;
  }
  return <Badge variant="outline">Not yet</Badge>;
}

export default function ProficiencyTab({ studentId }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ items: [], threshold: 2 });
  const [{ semester, school_year }, setPeriod] = useState(currentSemester());
  const [editing, setEditing] = useState(null); // current indicator row being edited

  const load = async () => {
    setLoading(true);
    try {
      const res = await getProficiencyForStudent(studentId);
      setData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load proficiency data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (studentId) load(); /* eslint-disable-line react-hooks/exhaustive-deps */ }, [studentId]);

  const grouped = useMemo(() => {
    const out = { academic: {}, transferable: {} };
    for (const item of data.items) {
      const type = item.indicator.type === "transferable" ? "transferable" : "academic";
      const subject = item.indicator.subject || "Other";
      if (!out[type][subject]) out[type][subject] = [];
      out[type][subject].push(item);
    }
    return out;
  }, [data.items]);

  const stats = useMemo(() => {
    const academic = data.items.filter((i) => i.indicator.type !== "transferable");
    const transferable = data.items.filter((i) => i.indicator.type === "transferable");
    return {
      academic_total: academic.length,
      academic_achieved: academic.filter((i) => i.achieved).length,
      transferable_total: transferable.length,
      transferable_achieved: transferable.filter((i) => i.achieved).length,
    };
  }, [data.items]);

  const handleOverride = async (indicator_id, newValue) => {
    try {
      await overrideAchieved({ student_id: studentId, indicator_id, achieved_override: newValue });
      toast.success(newValue === null ? "Override cleared" : `Marked ${newValue ? "Achieved" : "Not yet"} (manual)`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update");
    }
  };

  if (loading) {
    return <Card><CardContent className="py-12 text-center text-muted-foreground">Loading proficiency catalog…</CardContent></Card>;
  }

  const sectionFor = (type) => {
    const subjectMap = grouped[type] || {};
    const subjects = Object.keys(subjectMap).sort();
    if (subjects.length === 0) return <p className="text-sm text-muted-foreground py-6">No indicators in this section.</p>;
    return subjects.map((subject) => (
      <Card key={subject} className="overflow-hidden" data-testid={`prof-subject-${subject.replace(/\s+/g, "-").toLowerCase()}`}>
        <CardHeader className="bg-[#00305E]/5 py-3">
          <CardTitle className="text-base flex items-center justify-between">
            <span>{subject}</span>
            <span className="text-xs font-normal text-muted-foreground">
              {subjectMap[subject].filter((i) => i.achieved).length} / {subjectMap[subject].length} achieved
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="bg-muted/40">
              <tr className="border-b">
                <th className="text-left py-2 px-3 w-1/2">Indicator</th>
                <th className="text-left py-2 px-3">Latest ({semester})</th>
                <th className="text-left py-2 px-3">Cumulative 3.x</th>
                <th className="text-left py-2 px-3">Status</th>
                <th className="text-right py-2 px-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {subjectMap[subject].map((item) => (
                <tr key={item.indicator.id} className="border-b hover:bg-muted/20">
                  <td className="py-2 px-3">
                    <div className="font-medium text-foreground">
                      {item.indicator.category && <span className="text-xs text-muted-foreground mr-2">{item.indicator.category}</span>}
                      {item.indicator.indicator_label && <span className="text-xs font-semibold mr-1">{item.indicator.indicator_label}:</span>}
                      {item.indicator.indicator_text.length > 140
                        ? item.indicator.indicator_text.slice(0, 140) + "…"
                        : item.indicator.indicator_text}
                    </div>
                    {item.indicator.proficiency && item.indicator.indicator_text !== item.indicator.proficiency && (
                      <div className="text-xs text-muted-foreground mt-1 italic">
                        {item.indicator.proficiency}
                      </div>
                    )}
                  </td>
                  <td className="py-2 px-3 text-xs">
                    {item.latest?.score || <span className="text-muted-foreground">—</span>}
                  </td>
                  <td className="py-2 px-3 text-xs font-mono">
                    {type === "academic" ? `3.${item.cumulative_times_demonstrated}` : "—"}
                  </td>
                  <td className="py-2 px-3">
                    <StatusBadge achieved={item.achieved} override={item.achieved_override} />
                  </td>
                  <td className="py-2 px-3 text-right">
                    <Button
                      size="sm" variant="outline"
                      onClick={() => setEditing({ indicator: item.indicator, latest: item.latest, achieved: item.achieved, override: item.achieved_override })}
                      data-testid={`edit-indicator-${item.indicator.id}`}
                    >
                      Edit
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    ));
  };

  return (
    <div className="space-y-4" data-testid="proficiency-tab">
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <CardTitle>Proficiency Tracking</CardTitle>
              <CardDescription>Long-term proficiencies and transferable skills. Achieved status is auto-derived; toggle manual override per indicator.</CardDescription>
            </div>
            <div className="flex items-end gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Semester</Label>
                <Input className="w-36" value={semester} onChange={(e) => setPeriod((p) => ({ ...p, semester: e.target.value }))} data-testid="prof-semester-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">School Year</Label>
                <Input className="w-32" value={school_year} onChange={(e) => setPeriod((p) => ({ ...p, school_year: e.target.value }))} data-testid="prof-year-input" />
              </div>
            </div>
          </div>
          <div className="flex gap-3 flex-wrap pt-3">
            <Badge variant="secondary" data-testid="stat-academic">
              Academic: {stats.academic_achieved}/{stats.academic_total} achieved
            </Badge>
            <Badge variant="secondary" data-testid="stat-transferable">
              Transferable Skills: {stats.transferable_achieved}/{stats.transferable_total} achieved
            </Badge>
            <Badge variant="outline">Threshold: 3.{data.threshold || 2}</Badge>
          </div>
        </CardHeader>
      </Card>

      <Tabs defaultValue="academic">
        <TabsList>
          <TabsTrigger value="academic" data-testid="prof-section-academic">Academic & Electives</TabsTrigger>
          <TabsTrigger value="transferable" data-testid="prof-section-transferable">Transferable Skills</TabsTrigger>
        </TabsList>
        <TabsContent value="academic" className="space-y-4 pt-4">{sectionFor("academic")}</TabsContent>
        <TabsContent value="transferable" className="space-y-4 pt-4">{sectionFor("transferable")}</TabsContent>
      </Tabs>

      {editing && (
        <RecordDialog
          editing={editing}
          onClose={() => setEditing(null)}
          studentId={studentId}
          semester={semester}
          school_year={school_year}
          onSaved={() => { setEditing(null); load(); }}
          onOverride={(value) => { handleOverride(editing.indicator.id, value); setEditing(null); }}
        />
      )}
    </div>
  );
}

function RecordDialog({ editing, onClose, studentId, semester, school_year, onSaved, onOverride }) {
  const isTransferable = editing.indicator.type === "transferable";
  const scoreOptions = isTransferable ? TRANSFERABLE_SCORES : ACADEMIC_SCORES;
  const [score, setScore] = useState(editing.latest?.score || "");
  const [timesDemonstrated, setTimesDemonstrated] = useState(editing.latest?.times_demonstrated || 0);
  const [classwork, setClasswork] = useState(editing.latest?.classwork_description || "");
  const [notes, setNotes] = useState(editing.latest?.notes || "");
  const [thresholdOverride, setThresholdOverride] = useState(
    editing.student_override_min_demonstrations ?? "",
  );
  const [thresholdReason, setThresholdReason] = useState("");
  const [saving, setSaving] = useState(false);

  const baseMin = editing.indicator.min_demonstrations || 2;
  const effectiveMin = editing.effective_min_demonstrations ?? baseMin;

  const handleSaveThreshold = async () => {
    try {
      const value = thresholdOverride === "" ? null : Number(thresholdOverride);
      await setStudentThresholdOverride(studentId, {
        indicator_id: editing.indicator.id,
        override_min_demonstrations: value,
        reason: thresholdReason,
      });
      toast.success(value === null ? "Threshold reset to default" : "Per-student threshold saved");
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save threshold");
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await upsertProficiencyRecord({
        student_id: studentId,
        indicator_id: editing.indicator.id,
        semester,
        school_year,
        score: score || null,
        times_demonstrated: score === "3" ? Number(timesDemonstrated) || 1 : 0,
        classwork_description: classwork,
        notes,
      });
      toast.success("Record saved");
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Indicator — {semester}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="rounded-lg bg-muted/30 p-3 text-sm">
            <div className="font-semibold">{editing.indicator.subject} → {editing.indicator.category}</div>
            <div className="mt-1">{editing.indicator.indicator_text}</div>
          </div>

          <div className="space-y-2">
            <Label>Score</Label>
            <Select value={score || undefined} onValueChange={setScore}>
              <SelectTrigger data-testid="prof-score-select"><SelectValue placeholder="Select score" /></SelectTrigger>
              <SelectContent>
                {scoreOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {!isTransferable && score === "3" && (
            <div className="space-y-2">
              <Label>Times demonstrated this period (the .X in 3.X)</Label>
              <Input
                type="number" min={0}
                value={timesDemonstrated}
                onChange={(e) => setTimesDemonstrated(e.target.value)}
                data-testid="prof-times-input"
              />
              <p className="text-xs text-muted-foreground">Cumulative across semesters; threshold for Achieved is 3.{effectiveMin}.</p>
            </div>
          )}

          <div className="rounded-lg border-2 border-amber-300 bg-amber-50/40 p-3 space-y-2">
            <div className="text-xs font-semibold uppercase text-amber-800">Per-student threshold override</div>
            <p className="text-xs text-muted-foreground">
              Default for this indicator: <strong>{baseMin}</strong> demonstrations.
              Currently effective for this student: <strong>{effectiveMin}</strong>.
              Override the default for students with different social/enrollment circumstances (e.g., joined late).
            </p>
            <div className="flex items-end gap-2">
              <div className="space-y-1 flex-1">
                <Label className="text-xs">Override minimum (blank = use default)</Label>
                <Input
                  type="number" min={0}
                  value={thresholdOverride}
                  onChange={(e) => setThresholdOverride(e.target.value)}
                  placeholder={String(baseMin)}
                  data-testid="prof-threshold-override-input"
                />
              </div>
              <div className="space-y-1 flex-[2]">
                <Label className="text-xs">Reason (optional)</Label>
                <Input
                  value={thresholdReason}
                  onChange={(e) => setThresholdReason(e.target.value)}
                  placeholder="e.g., enrolled mid-semester"
                  data-testid="prof-threshold-reason-input"
                />
              </div>
              <Button size="sm" variant="outline" onClick={handleSaveThreshold} data-testid="prof-save-threshold-btn">
                Save threshold
              </Button>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Work offered / Classwork description</Label>
            <textarea
              value={classwork} onChange={(e) => setClasswork(e.target.value)}
              className="w-full min-h-[80px] rounded-md border bg-background p-2 text-sm"
              data-testid="prof-classwork-input"
              placeholder="What classwork or assessment was offered?"
            />
          </div>

          <div className="space-y-2">
            <Label>Notes (optional)</Label>
            <textarea
              value={notes} onChange={(e) => setNotes(e.target.value)}
              className="w-full min-h-[60px] rounded-md border bg-background p-2 text-sm"
              data-testid="prof-notes-input"
            />
          </div>

          <div className="rounded-lg border-2 border-dashed p-3 space-y-2">
            <div className="text-xs font-semibold uppercase text-muted-foreground">Manual override</div>
            <p className="text-xs text-muted-foreground">
              Current status: {editing.achieved ? "Achieved" : "Not yet"} ({editing.override === null || editing.override === undefined ? "auto" : "manual"})
            </p>
            <div className="flex gap-2 flex-wrap">
              <Button size="sm" variant="outline" onClick={() => onOverride(true)} data-testid="prof-force-achieved">Force Achieved</Button>
              <Button size="sm" variant="outline" onClick={() => onOverride(false)} data-testid="prof-force-not-yet">Force Not Yet</Button>
              <Button size="sm" variant="ghost" onClick={() => onOverride(null)} data-testid="prof-clear-override">Clear override (use auto)</Button>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-[#00305E] hover:bg-[#00305E]/90" data-testid="prof-save-btn">
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
